import { useState, useEffect } from "react";
import { Button } from "antd";
import { FileOutlined, DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import { refreshFacebookMessageAttachments } from "@api/services/facebook";
import { useAppDispatch } from "@stores/redux/store";
import { updateMessageUrl } from "@features/messages/messagesSlice";
import { useNotificationContext } from "@hooks/useNotificationContext";
import styles from "./styles.module.less";

interface MessageFileProps {
  fileUrl?: string;
  fileName: string;
  onDownload?: (url: string) => void;
  messageId?: string;
  conversationId?: string;
  pageId?: string | null;
}

export const MessageFile = ({
  fileUrl,
  fileName,
  onDownload,
  messageId,
  conversationId,
  pageId,
}: MessageFileProps) => {
  const { success, error: showError } = useNotificationContext();
  const [fileError, setFileError] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(fileUrl);
  const dispatch = useAppDispatch();

  const checkUrlExpired = async (url: string) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(url, {
        method: "HEAD",
        signal: controller.signal,
        cache: "no-store",
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        setFileError(true);
      } else {
        setFileError(false);
      }
    } catch (error) {
      console.error("Error checking file URL:", error);
      setFileError(true);
    }
  };

  useEffect(() => {
    if (fileUrl) {
      setCurrentUrl(fileUrl);
      setFileError(false);
      if (fileUrl && messageId && pageId) {
        checkUrlExpired(fileUrl);
      }
    }
  }, [fileUrl, messageId, pageId]);

  const handleRetry = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (!messageId || !pageId) {
      showError("Không tìm thấy thông tin message hoặc page");
      return;
    }

    setIsRefreshing(true);
    try {
      const response = await refreshFacebookMessageAttachments({
        messageId,
        pageId,
      });

      if (response.code === 0) {
        const newUrl = response.data?.newUrl;
        if (newUrl && messageId && conversationId) {
          dispatch(
            updateMessageUrl({
              conversationId,
              messageId,
              newUrl,
            })
          );
          setCurrentUrl(newUrl);
          setFileError(false);
          success("Đã tải lại file thành công");
        } else {
          success("Đang tải lại file...");
          setFileError(false);
        }
      } else {
        showError(response.message || "Tải lại file thất bại");
      }
    } catch (error) {
      console.error("Error refreshing file attachments:", error);
      showError("Tải lại file thất bại");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleFileClick = () => {
    if (fileError) {
      return;
    }
    if (onDownload && currentUrl) {
      onDownload(currentUrl);
    } else if (currentUrl) {
      window.open(currentUrl, "_blank");
    }
  };

  const handleDownloadClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (fileError) {
      await handleRetry(e);
      return;
    }

    if (onDownload && currentUrl) {
      try {
        await onDownload(currentUrl);
      } catch (error) {
        console.error("Error downloading file:", error);
        setFileError(true);
      }
    } else if (currentUrl) {
      try {
        const response = await fetch(currentUrl, { method: "HEAD" });
        if (!response.ok) {
          setFileError(true);
          return;
        }
        window.open(currentUrl, "_blank");
      } catch (error) {
        console.error("Error opening file:", error);
        setFileError(true);
      }
    }
  };

  return (
    <div
      className={styles.fileMessage}
      onClick={handleFileClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleFileClick();
        }
      }}
    >
      <div className={styles.fileIconWrapper}>
        <div className={styles.fileIcon}>
          <FileOutlined style={{ fontSize: 26 }} />
        </div>
      </div>
      <div className={styles.fileInfo}>
        <span className={styles.fileName}>{fileName}</span>
        <span className={styles.fileType}>Tệp đính kèm</span>
      </div>
      {fileError && messageId && pageId ? (
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          className={styles.retryButton}
          size="small"
          onClick={handleRetry}
          loading={isRefreshing}
          disabled={isRefreshing}
        >
          Tải lại
        </Button>
      ) : (
        currentUrl && (
          <Button
            type="text"
            icon={<DownloadOutlined />}
            className={styles.downloadButton}
            onClick={handleDownloadClick}
          />
        )
      )}
    </div>
  );
};
